import { useTranslation } from 'react-i18next'
import { useContext, useEffect, useState, useRef } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Breadcrumbs, ListItemButton, Typography } from '@mui/material'

import { Customer, Payment } from '../types'
import NextIcon from '../assets/icons/next-icon.svg'
import { requestAccessToken } from '../api/auth.api'
import { getCustomerCompanyPockets } from '../api/customers.api'
import { AuthContext, UserContext } from '../contexts'
import { countriesCurrencies, PaymentWithNoveltiesType } from '../lib'
import { getPaymentWithAlertsDetailsData } from '../api/app-data.api'
import { PaymentWithAlertsDetailsTable } from '../components/payment-with-alerts-details-management/payment-with-alerts-details-table'

const PaymentWithAlertsDetailsManagement = () => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
  const selectedCurrency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

  const navigate = useNavigate()
  const location = useLocation()
  const { userSession } = useContext(UserContext)
  const dataFullyLoadedRef = useRef<boolean>(false)
  const { paymentId, customerCompanyId } = useParams()
  const { isAuth, removeAuth } = useContext(AuthContext)
  const customerCompanyPocketsRef = useRef<Array<any>>([])
  const { t } = useTranslation(['customer-company-details-management', 'payments-to-review-management'])

  const [topDistance, setTopDistance] = useState<number>(0)
  const [paymentData, setPaymentData] = useState<Payment | null>(null)
  const [loadingLocalData, setLoadingLocalData] = useState<boolean>(true)
  const [paymentCustomerData, setPaymentCustomerData] = useState<Customer | null>(null)
  const [selectedMainFilter, setSelectedMainFilter] = useState<PaymentWithNoveltiesType>(PaymentWithNoveltiesType.WITH_FINDINGS)

  useEffect(() => {
    if (!userSession) {
      return
    }

    if (userSession && userSession?.role !== 'super-admin') {
      navigate('/404')
      return
    }

    handleGetPaymentWithAlertsDetails()

    const searchParams = new URLSearchParams(location.search)

    const n = searchParams.get('n')
    const filter = searchParams.get('filter')

    if (filter) setSelectedMainFilter(Number(filter))
    if (n) setTopDistance(Number(n))

    handleGetCustomerCompanyPockets()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession])

  const handleGetPaymentWithAlertsDetails = async () => {
    let localPaymentWithAlertsToSet: any = {}
    const localPaymentWithAlerts = window.sessionStorage.getItem(`paymentWithAlertsData:${paymentId}`)

    if (localPaymentWithAlerts) {
      localPaymentWithAlertsToSet = JSON.parse(localPaymentWithAlerts)
      handleSetInitialData(localPaymentWithAlertsToSet)
    }

    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const { data } = await getPaymentWithAlertsDetailsData(String(paymentId), accessToken)
      console.log('data', data)

      if (Object.keys(data).length === 0) {
        navigate(`/payments-with-alerts/${customerCompanyId}`)
        return
      }

      handleSetInitialData(data)
      window.sessionStorage.setItem(`paymentWithAlertsData:${paymentId}`, JSON.stringify(data))
    } catch (error) {
      console.log('Error handleGetPaymentWithAlertsDetails > ', error)
    }
  }

  const handleSetInitialData = async (dataToSet: any) => {
    const paymentCurrency = dataToSet.paymentWithAlerts.currency ? dataToSet.paymentWithAlerts.currency : 'cop'

    if (paymentCurrency !== selectedCurrency) {
      navigate(`/payments-with-alerts/${customerCompanyId}`)
      return
    }

    setPaymentCustomerData(dataToSet.customerData)
    setPaymentData(dataToSet.paymentWithAlerts)
    setLoadingLocalData(false)
    dataFullyLoadedRef.current = true
  }

  const handleGetCustomerCompanyPockets = async () => {
    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))
      const {
        data: { customerCompanyPockets }
      } = await getCustomerCompanyPockets(String(customerCompanyId), 'cop', accessToken)

      customerCompanyPocketsRef.current = customerCompanyPockets
    } catch (error: any) {
      console.log(error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      }
    }
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <div className="section-container">
      <Breadcrumbs
        separator={
          <img
            src={NextIcon}
            alt="next-icon"
          />
        }
        sx={{ width: '100%' }}
        aria-label="breadcrumb"
      >
        <ListItemButton
          sx={{
            '&.MuiButtonBase-root': {
              padding: '0px'
            },
            '&.MuiButtonBase-root:hover': {
              backgroundColor: 'rgba(0,0,0,0)'
            }
          }}
          onClick={() => navigate('/customer-companies')}
        >
          <Typography
            key="1"
            className="breadcrumb-inactive"
          >
            {t('breadcrumbs.firstLevel')}
          </Typography>
        </ListItemButton>
        <ListItemButton
          sx={{
            '&.MuiButtonBase-root': {
              padding: '0px'
            },
            '&.MuiButtonBase-root:hover': {
              backgroundColor: 'rgba(0,0,0,0)'
            }
          }}
          onClick={() => navigate(`/customer-company-details/${customerCompanyId}`)}
        >
          <Typography
            key="2"
            className="breadcrumb-inactive"
          >
            {t('breadcrumbs.secondLevel')}
          </Typography>
        </ListItemButton>
        <ListItemButton
          sx={{
            '&.MuiButtonBase-root': {
              padding: '0px'
            },
            '&.MuiButtonBase-root:hover': {
              backgroundColor: 'rgba(0,0,0,0)'
            }
          }}
          onClick={() => {
            if (!isNaN(selectedMainFilter) && !isNaN(topDistance)) {
              navigate(`/payments-with-alerts/${customerCompanyId}?filter=${selectedMainFilter}&n=${Math.round(Number(topDistance))}`)
              return
            }

            navigate(`/payments-with-alerts/${customerCompanyId}`)
          }}
        >
          <Typography
            key="3"
            className="breadcrumb-inactive"
          >
            {t('breadcrumbs.withAlerts')}
          </Typography>
        </ListItemButton>
      </Breadcrumbs>
      {loadingLocalData ? (
        <div className="payment-to-review-details-container">
          <div className="payment-to-review-details-title">{t('title', { ns: 'payments-to-review-management' })}</div>
          <div className="documents-list-container">
            <div className="documents-list-item-data-container-loader"></div>
            <div className="documents-list-item-data-container-loader"></div>
            <div className="documents-list-item-data-container-loader"></div>
            <div className="documents-list-item-data-container-loader"></div>
          </div>
        </div>
      ) : (
        <>
          <div className="payment-to-review-details-container">
            <div className="payment-to-review-details-title">{t('title', { ns: 'payments-to-review-management' })}</div>
            <PaymentWithAlertsDetailsTable
              paymentData={paymentData}
              paymentCustomerData={paymentCustomerData}
              additionalInfo={customerCompanyPocketsRef.current}
            />
          </div>
        </>
      )}
    </div>
  )
}

export default PaymentWithAlertsDetailsManagement
